import React, { useMemo, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import TextField from "@material-ui/core/TextField";

import { Bar, Btn, Lbl, Strip } from "./ui";

/**
 * Publicar el mismo anuncio en varios países, con presupuesto unificado o uno por país.
 *
 * Son dos estructuras distintas en Meta y conviene decirlo explícito porque la diferencia no es
 * cosmética:
 *
 *  - UNIFICADO: un solo conjunto con todos los países. Meta decide dónde gastar. Concentra las
 *    conversiones, que es lo que necesita el aprendizaje.
 *  - POR PAÍS: un conjunto por país, cada uno con su monto. Da control del reparto y permite
 *    poner más donde el CPA es mejor, al precio de fragmentar el aprendizaje.
 *
 * Toma el conjunto actual como plantilla: se conservan edades, públicos, optimización y anuncios.
 */
export default function RepartirPaises({ abierto, paises, plantilla, onAplicar, onCerrar }) {
  const [modo, setModo] = useState("por_pais");
  const [elegidos, setElegidos] = useState((plantilla && plantilla.paises) || []);
  const [montos, setMontos] = useState({});
  const [unificado, setUnificado] = useState(
    plantilla && plantilla.presupuesto_diario ? String(plantilla.presupuesto_diario) : ""
  );
  const [aTodos, setATodos] = useState("");
  const [busqueda, setBusqueda] = useState("");

  const visibles = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return paises;
    return paises.filter(
      (p) => p.iso.toLowerCase().includes(q) || (p.nombre || "").toLowerCase().includes(q)
    );
  }, [paises, busqueda]);

  const toggle = (iso) =>
    setElegidos((prev) => (prev.includes(iso) ? prev.filter((x) => x !== iso) : [...prev, iso]));

  const total = elegidos.reduce((s, iso) => s + (Number(montos[iso]) || 0), 0);

  function aplicar() {
    const base = plantilla || {};

    if (modo === "unificado") {
      onAplicar([
        {
          ...base,
          nombre: `${base.nombre || "Conjunto"} · ${elegidos.join("+")}`,
          paises: elegidos,
          presupuesto_diario: Number(unificado) || 0,
        },
      ]);
      return;
    }

    // Un conjunto por país. El nombre lleva el ISO para poder leerlos en el panel de Meta.
    onAplicar(
      elegidos.map((iso) => ({
        ...base,
        nombre: `${(base.nombre || "Conjunto").replace(/\s*·\s*[A-Z+]+$/, "")} · ${iso}`,
        paises: [iso],
        presupuesto_diario: Number(montos[iso]) || 0,
      }))
    );
  }

  const listo =
    elegidos.length > 0 &&
    (modo === "unificado" ? Number(unificado) > 0 : elegidos.every((iso) => Number(montos[iso]) > 0));

  return (
    <Dialog open={!!abierto} onClose={onCerrar} maxWidth="sm" fullWidth>
      <DialogContent className="ma-root" style={{ padding: 12 }}>
        <Bar style={{ marginBottom: 8 }}>
          <b style={{ fontSize: 13 }}>Publicar en varios países</b>
          <div style={{ flex: 1 }} />
          <Btn onClick={onCerrar}>cerrar</Btn>
        </Bar>

        <Bar style={{ marginBottom: 8 }}>
          <Btn on={modo === "por_pais"} onClick={() => setModo("por_pais")}>presupuesto por país</Btn>
          <Btn on={modo === "unificado"} onClick={() => setModo("unificado")}>presupuesto unificado</Btn>
        </Bar>

        <Strip tone={modo === "unificado" ? "ok" : "warn"} style={{ marginBottom: 8 }}>
          {modo === "unificado" ? (
            <>
              <b>Un solo conjunto</b> con todos los países. Meta decide dónde gastar. Concentra las
              conversiones, que es lo que el aprendizaje necesita en esta cuenta.
            </>
          ) : (
            <>
              <b>Un conjunto por país</b>, cada uno con su monto. Te da el control del reparto, pero
              divide las conversiones entre más conjuntos y ninguno va a salir de aprendizaje.
            </>
          )}
        </Strip>

        <TextField
          placeholder="filtrar países"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          fullWidth
          style={{ marginBottom: 6 }}
        />

        <div style={{ maxHeight: "38vh", overflowY: "auto", border: "1px solid #E3E8F0", borderRadius: 6, padding: 6 }}>
          {visibles.map((p) => {
            const on = elegidos.includes(p.iso);
            return (
              <div
                key={p.iso}
                style={{
                  display: "flex", alignItems: "center", gap: 8, padding: "3px 4px",
                  borderBottom: "1px solid #F4F7FB",
                }}
              >
                <Btn on={on} onClick={() => toggle(p.iso)} style={{ minWidth: 46 }}>{p.iso}</Btn>
                <span style={{ flex: 1, fontSize: 12.5, color: on ? "#24303F" : "#8A96A8" }}>{p.nombre}</span>

                {modo === "por_pais" && on && (
                  <TextField
                    type="number"
                    placeholder="USD/día"
                    value={montos[p.iso] || ""}
                    onChange={(e) => setMontos({ ...montos, [p.iso]: e.target.value })}
                    style={{ width: 88 }}
                  />
                )}
              </div>
            );
          })}
        </div>

        <Bar style={{ marginTop: 10, marginBottom: 0 }}>
          {modo === "unificado" ? (
            <div>
              <Lbl>presupuesto diario total (USD)</Lbl>
              <TextField type="number" value={unificado} onChange={(e) => setUnificado(e.target.value)} style={{ width: 130 }} />
            </div>
          ) : (
            <>
              <div>
                <Lbl>poner el mismo monto a todos</Lbl>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <TextField type="number" value={aTodos} onChange={(e) => setATodos(e.target.value)} style={{ width: 90 }} />
                  <Btn
                    onClick={() => {
                      const m = { ...montos };
                      elegidos.forEach((iso) => (m[iso] = aTodos));
                      setMontos(m);
                    }}
                    disabled={!aTodos || elegidos.length === 0}
                  >
                    aplicar
                  </Btn>
                </div>
              </div>
              <span style={{ fontSize: 12, color: "#7A8699" }}>
                {elegidos.length} país(es) · total ${total.toLocaleString("es-AR")}/día
              </span>
            </>
          )}

          <div style={{ flex: 1 }} />
          <Btn tone="ok" onClick={aplicar} disabled={!listo}>
            {modo === "unificado" ? "crear 1 conjunto" : `crear ${elegidos.length} conjunto(s)`}
          </Btn>
        </Bar>

        {!listo && elegidos.length > 0 && (
          <div style={{ fontSize: 11.5, color: "#B42318", marginTop: 6 }}>
            {modo === "unificado" ? "Falta el presupuesto." : "Falta el presupuesto de algún país."}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
